// client/src/components/public/PublicEventPage.jsx
//
// The standalone public event page — /ev/:tenantSlug/:eventSlug. No auth, no
// app chrome. Hero (headline + date + venue), description, "what's on" with
// per-item CTAs, and add-to-calendar. On-Screen 2.0.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchPublicEvent } from '../../services/publicEventApi';
import { assetUrl } from '../../utils/assetUrl';

const KIND_LABEL = {
  ticketed_event: 'Get tickets',
  auction: 'View auction',
  raffle: 'Enter raffle',
  challenge: 'Join challenge',
  contest: 'Enter contest',
  live_qna: 'Live Q&A',
  poll_quiz: 'Poll / quiz',
  ugc_campaign: 'Share your photos',
  ama: 'Ask me anything',
  watch_party: 'Watch party',
  collab_takeover: 'Takeover',
  workshop: 'Workshop',
  themed_day: 'Themed day',
};

function fmtRange(start, end, tz) {
  if (!start) return null;
  const opts = { dateStyle: 'full', timeStyle: 'short', timeZone: tz || undefined };
  try {
    const s = new Date(String(start).replace(' ', 'T') + 'Z');
    const e = end ? new Date(String(end).replace(' ', 'T') + 'Z') : null;
    const sf = s.toLocaleString(undefined, opts);
    if (!e || Number.isNaN(e.getTime())) return sf;
    const sameDay = s.toDateString() === e.toDateString();
    const ef = e.toLocaleString(undefined, sameDay ? { timeStyle: 'short', timeZone: tz || undefined } : opts);
    return `${sf} – ${ef}`;
  } catch {
    return String(start);
  }
}

export default function PublicEventPage() {
  const { tenantSlug, eventSlug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // This is a public page — it must not wear the app's theme. index.css and
  // overrides.css force body/headings white and `html.theme-*` rules pin text
  // colour with !important. Drop those classes while the page is mounted and
  // restore them on unmount.
  useEffect(() => {
    const html = document.documentElement;
    const removed = [...html.classList].filter((c) => c.startsWith('theme-'));
    removed.forEach((c) => html.classList.remove(c));
    return () => removed.forEach((c) => html.classList.add(c));
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchPublicEvent(tenantSlug, eventSlug)
      .then((d) => { if (alive) { setData(d); document.title = d?.event?.name || 'Event'; } })
      .catch((e) => { if (alive) setError(e?.response?.status === 404 ? 'not-found' : 'error'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tenantSlug, eventSlug]);

  if (loading) {
    return <div style={wrap}><p style={{ opacity: 0.6 }}>Loading…</p></div>;
  }
  if (error === 'not-found') {
    return <div style={wrap}><h1 style={{ fontSize: 22 }}>Event not found</h1><p style={{ opacity: 0.7 }}>This event page is private or the link is wrong.</p></div>;
  }
  if (error || !data?.success) {
    return <div style={wrap}><p style={{ color: '#c0392b' }}>Something went wrong loading this event.</p></div>;
  }

  const { tenant, event, whatsOn, calendar } = data;

  return (
    <div className="os-public-event" style={wrap}>
      {/* This is a PUBLIC page — it must not inherit the app's dark theme
          (index.css sets body{color:#fff} and h1..h6{color:#fff;text-align:center}).
          Scope a light palette to this subtree, winning on specificity. */}
      <style>{`
        .os-public-event, .os-public-event * { color: #1f2937 !important; }
        .os-public-event h1, .os-public-event h2 {
          color: #0f172a !important; text-align: left !important;
          font-family: inherit !important; font-weight: 700 !important;
        }
        .os-public-event h1 { font-size: 30px; line-height: 1.2; margin: 0 0 8px; }
        .os-public-event h2 { font-size: 18px; margin: 0 0 12px; }
        .os-public-event a { color: #1d4ed8 !important; text-decoration: none; }
        .os-public-event a.os-btn-primary, .os-public-event a.os-btn-primary * { color: #fff !important; }
        .os-public-event .os-muted, .os-public-event .os-muted * { color: #6b7280 !important; }
        .os-public-event .os-venue { color: #374151 !important; }
        .os-public-event .os-foot, .os-public-event .os-foot * { color: #9ca3af !important; }
      `}</style>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 18px 64px' }}>
        {tenant.logo && (
          <img src={assetUrl(tenant.logo)} alt={tenant.name} style={{ height: 40, marginBottom: 20 }} />
        )}

        {event.visibility === 'unlisted' && (
          <div style={pill}>Unlisted preview</div>
        )}

        {event.headline && <p className="os-muted" style={{ margin: '0 0 4px', fontWeight: 600 }}>{event.headline}</p>}
        <h1>{event.name}</h1>

        <p style={{ margin: '0 0 4px', fontWeight: 600 }}>{fmtRange(event.startdate, event.enddate, event.timezone)}</p>

        {event.venue && (
          <p className="os-venue" style={{ margin: '0 0 16px' }}>
            {event.venue.name && <strong>{event.venue.name}</strong>}
            {event.venue.name && event.venue.address ? ' · ' : ''}
            {event.venue.address}
            {event.venue.mapUrl && (
              <> · <a href={event.venue.mapUrl} target="_blank" rel="noreferrer">Map</a></>
            )}
          </p>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '16px 0 24px' }}>
          {event.cta && (
            <a className="os-btn-primary" href={event.cta.url} target="_blank" rel="noreferrer" style={btnPrimary}>{event.cta.label}</a>
          )}
          {calendar?.google && (
            <a href={calendar.google} target="_blank" rel="noreferrer" style={btnGhost}>Add to Google Calendar</a>
          )}
          {calendar?.ics && (
            <a href={calendar.ics} style={btnGhost}>Download .ics</a>
          )}
        </div>

        {event.description && (
          <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{event.description}</p>
        )}

        {whatsOn && whatsOn.length > 0 && (
          <section style={{ marginTop: 32 }}>
            <h2>What's on</h2>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {whatsOn.map((it) => (
                <li key={it.id} style={itemCard}>
                  <div style={{ fontWeight: 700 }}>{it.name}</div>
                  {it.description && <div className="os-muted" style={{ fontSize: 14, marginTop: 2 }}>{it.description}</div>}
                  {it.actions.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                      {it.actions.map((a, i) => (
                        a.url
                          ? <a key={i} href={a.url} target="_blank" rel="noreferrer" style={btnSmall}>{KIND_LABEL[a.kind] || a.kind}</a>
                          : <span key={i} style={{ ...btnSmall, opacity: 0.6, cursor: 'default' }}>{KIND_LABEL[a.kind] || a.kind}</span>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        <footer className="os-foot" style={{ marginTop: 48, paddingTop: 16, borderTop: '1px solid #e5e7eb', fontSize: 13 }}>
          Hosted by {tenant.website ? <a href={tenant.website} target="_blank" rel="noreferrer">{tenant.name}</a> : tenant.name}
          {' · '}Powered by On-Screen
        </footer>
      </div>
    </div>
  );
}

const wrap = {
  minHeight: '100vh',
  background: '#ffffff',
  color: '#111827',
  fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
};
const pill = { display: 'inline-block', background: '#fef3c7', color: '#92400e', fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 999, marginBottom: 12 };
const btnPrimary = { background: '#1d4ed8', color: '#fff', padding: '10px 18px', borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: 14 };
const btnGhost = { border: '1px solid #d1d5db', color: '#374151', padding: '10px 16px', borderRadius: 8, textDecoration: 'none', fontSize: 14 };
const btnSmall = { border: '1px solid #d1d5db', color: '#1d4ed8', padding: '5px 10px', borderRadius: 6, textDecoration: 'none', fontSize: 13 };
const itemCard = { border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 10 };
